<template>
  <div>
    <q-dialog v-model="isDialogueVisible" @hide="resetData">
      <q-card class="new-policy-card">
        <q-form @submit.prevent="onSubmit" @reset="onReset" class="q-gutter-md">
          <q-card-section class="title">
            <div v-if="duplicateCurrent">Create new policy from {{ policyId }}</div>
            <div v-else>Create new policy</div>
          </q-card-section>

          <q-scroll-area class="scroll-area">
            <q-card-section class="content-wrapper">
              <q-input
                :error="v$.name.$error && !v$.name.$pending"
                :error-message="
                  v$.name.required.$invalid
                    ? v$.name.required.$message
                    : v$.name.minLength.$invalid
                      ? v$.name.minLength.$message
                      : v$.name.maxLength.$invalid
                        ? v$.name.maxLength.$message
                        : v$.name.isUnique.$invalid
                          ? v$.name.isUnique.$message
                          : undefined
                "
                :loading="v$.name.isUnique.$pending"
                autofocus
                bottom-slots
                class="q-mt-sm q-mb-md"
                debounce="500"
                filled
                label="Name"
                placeholder="e.g.: global_pay_later"
                type="text"
                v-model="state.name"
              >
                <template #hint v-if="state.name && state.name !== normalizedName">
                  Will be normalized to <b>{{ normalizedName }}</b>
                </template>
              </q-input>

              <q-select
                :error-message="v$.apiSchema.required.$message"
                :error="v$.apiSchema.$error"
                filled
                bottom-slots
                v-model="state.apiSchema"
                :options="apiSchemas"
                label="API Schema"
              >
                <template #hint v-if="state.apiSchema">
                  <a @click.capture.stop class="hint-link" :href="apiLink" target="_blank">
                    More about {{ state.apiSchema }} schema
                    <q-icon name="fas fa-arrow-right" />
                  </a>
                </template>
              </q-select>

              <q-select
                class="q-mt-md q-mb-md"
                filled
                v-model="state.deploymentRegion"
                :options="regions"
                label="Deployment Region"
                :error-message="v$.deploymentRegion.required.$message"
                :error="v$.deploymentRegion.$error"
              />

              <q-input
                :error-message="v$.issue.required.$message"
                :error="v$.issue.$error"
                @keyup.enter="prompt = false"
                class="q-mt-md q-mb-md"
                filled
                label="Issue reference"
                placeholder="e.g.: Q2200946 or IRIS-4279"
                type="text"
                v-model="state.issue"
              >
                <template #hint v-if="state.issue && state.issue !== normalizedIssue">
                  Will be normalized to <b>{{ normalizedIssue }}</b>
                </template>
              </q-input>

              <q-input
                :error-message="v$.description.required.$message"
                :error="v$.description.$error"
                @keyup.enter="prompt = false"
                dense
                filled
                label="Description"
                placeholder="A short explanation of the purpose of this policy"
                size="sm"
                type="textarea"
                v-model="state.description"
              />
            </q-card-section>
          </q-scroll-area>

          <q-separator class="separator" />
          <q-card-actions align="right" class="text-secondary card-actions">
            <q-btn class="btn-cancel" flat label="Cancel" v-close-popup />
            <q-btn
              :disable="v$.$invalid"
              :loading="isLoading"
              class="btn-add"
              color="secondary"
              label="Create"
              type="submit"
            />
          </q-card-actions>
        </q-form>
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { isProduction } from '@/environment'
import { paths } from '@/router'
import { apiSchemas, regions } from '@/utils/constants'
import { normalizeIssueRef } from '@/utils/issues'
import notifications from '@/utils/notifications'
import { getApiLink, normalizeToValidId, useDirectStore } from '@/utils/utils'
import type { ApiSchema, DeploymentRegion } from '@klarna/cbrms-policy-rules-manager-client-ts'
import { useVuelidate } from '@vuelidate/core'
import { helpers, maxLength, minLength, required } from '@vuelidate/validators'
import { isAxiosError } from 'axios'
import { computed, defineComponent, reactive } from 'vue'
import { useRouter } from 'vue-router/composables'

interface Props {
  visible: boolean
  policyNames: string[]
  duplicateCurrent: boolean
}

export default defineComponent<Props>({
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    policyNames: {
      type: Array,
      default: function () {
        return []
      },
    },
    duplicateCurrent: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, ctx) {
    const { state, dispatch } = useDirectStore()
    const router = useRouter()

    const isLoading = computed(() => state.policy.isLoading)
    const isDialogueVisible = computed({
      get: () => props.visible,
      set: val => {
        if (!val) {
          ctx.emit('close')
        }
      },
    })

    async function isPolicyNameUnique(value: string) {
      if (!value) {
        return true
      }

      if (v$.value.name.$invalid) {
        return true
      }

      try {
        await dispatch.policy.GET_POLICY({ id: normalizedName.value })
        // `GET /policies/{policyId}` succeeded (HTTP 200)
        // Which means that the specified rule name is already in used
        return false
      } catch (err) {
        // `GET /policies/{policyId}` returned a non-200 response
        // If we got a 404, it means the policy name is not in used and the validator should return `true`
        return isAxiosError(err) && err.response?.status === 404
      }
    }

    const data = reactive({
      apiSchema: undefined as ApiSchema | undefined,
      description: '',
      deploymentRegion: undefined as DeploymentRegion | undefined,
      issue: '',
      labels: [],
      name: '',
    })

    const rules = computed(() => ({
      apiSchema: { required },
      deploymentRegion: { required },
      description: { required },
      issue: { required },
      name: {
        isUnique: helpers.withMessage(
          ({ $model }) =>
            `Policy with that name already exists${
              $model !== normalizedName.value ? ` (as ${normalizedName.value})` : ''
            }`,
          helpers.withAsync(isPolicyNameUnique)
        ),
        maxLength: maxLength(60),
        minLength: minLength(2),
        required,
      },
    }))

    const normalizedIssue = computed(() => normalizeIssueRef(data.issue ?? ''))
    const normalizedName = computed(() => normalizeToValidId(data.name ?? ''))

    const v$ = useVuelidate(rules, data, { $autoDirty: true, $stopPropagation: true })

    function resetData() {
      data.apiSchema = undefined
      data.deploymentRegion = undefined
      data.description = ''
      data.name = ''
      data.issue = ''
      data.labels = []

      v$.value.$reset()
    }

    async function onSubmit() {
      if (!v$.value.$invalid) {
        const currentPolicy = state.policy.currentPolicy

        const rules = props.duplicateCurrent ? currentPolicy.rules : []
        const unittests = props.duplicateCurrent ? currentPolicy.unittests : []
        const contentInputs = props.duplicateCurrent ? currentPolicy.content_inputs : []
        const contentHelpers = props.duplicateCurrent ? currentPolicy.content_helpers : []
        const formatVersion = props.duplicateCurrent ? currentPolicy.format_version : undefined

        const payload = {
          api: data.apiSchema as ApiSchema,
          content_helpers: contentHelpers,
          content_inputs: contentInputs,
          deployment_region: data.deploymentRegion as DeploymentRegion,
          description: data.description,
          format_version: formatVersion,
          id: normalizedName.value,
          jira_issue: normalizedIssue.value,
          labels: data.labels,
          production_enabled: isProduction,
          rules,
          unittests,
        }
        await dispatch.policy.CREATE_POLICY(payload)

        if (props.duplicateCurrent) {
          dispatch.SHOW_NOTIFICATION({
            message: notifications.successfullyDuplicated(currentPolicy.id, normalizedName.value),
          })
        }

        router.push(
          paths.policyBuilder.replace(':policy_id', normalizedName.value).replace(':branch?', state.auth.user!.username)
        )
      }
    }

    return {
      apiLink: computed(() => getApiLink(data.apiSchema)),
      apiSchemas,
      isDialogueVisible,
      isLoading,
      normalizedName,
      normalizedIssue,
      onReset: () => undefined,
      onSubmit,
      paths,
      policyId: computed(() => state.policy.currentPolicy.id),
      regions,
      state: data,
      resetData,
      v$,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/styles/quasar.variables.scss';

.new-policy-card {
  flex: 1;
  min-width: 350px;
  position: relative;
  overflow: hidden;
}

.title {
  width: 100%;
  padding: 0.5em 1em;
  font-size: 15px;
  background-color: $info;
  color: white;
  position: sticky;
  top: 0;
  overflow: hidden;
  font-weight: 600;
  text-overflow: ellipsis;
}

.content-wrapper {
  padding: 0.1em 1em;
  flex: 1;
}

.scroll-area {
  height: 70vh;
}

.card-actions {
  position: sticky;
  bottom: 0;
  padding: 0 1em 1em;
}

.separator {
  margin: 1vh 0;
}

.dialogue-head {
  display: flex;
  justify-content: space-between;
}

.hint-link {
  color: $info;
  text-decoration: none;
  font-weight: 600;
  &:visited,
  &:active {
    color: $info;
    text-decoration: none;
  }
}
</style>
